import { useState, useEffect } from 'react';
import { FaSync, FaEllipsisV, FaUser } from 'react-icons/fa';
import { Menu, MenuItem } from '@mui/material';
import axios, { getBaseUrl } from '../../services/api';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/modals/ConfirmModal';
import editIcon from '../../styles/edit_icon.png';
import { useTranslation } from 'react-i18next';
import QRCodeModal from '../../components/modals/QRCodeModal';
import ECardModal from './modals/ECardModal';

export default function EcardSetting() {
  const { t } = useTranslation();
  const [ecards, setECards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [selectedECard, setSelectedECard] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchECards();
  }, []);

  const fetchECards = async () => {
    try {
      const response = await axios.get('/ecards');
      setECards(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchECards();
    } catch (error) {
      console.error(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredECards = ecards.filter(ecard => {
    const matchesSearch =
      ecard.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ecard.sipAccount.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const handleDelete = async () => {
    try {
      await axios.delete(`/ecards/${selectedECard._id}`);
      toast.success(t('Delete Successfully'));
      fetchECards();
      setIsConfirmOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleMenuClick = (event, ecard) => {
    setAnchorEl(event.currentTarget);
    setSelectedECard(ecard);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="p-6 bg-gradient-to-br from-skin-base to-skin-secondary container-height app-text">
      <div className="flex justify-between items-center bg-skin-primary p-4 gap-4 mb-6 rounded-xl shadow-lg h-filter">
        <div className="flex-1 flex items-center gap-4">
          <input
            type="text"
            placeholder={t('Search ECards...')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          <button
            onClick={handleRefresh}
            className="p-2 text-gray-600 hover:text-gray-800"
            disabled={isRefreshing}
          >
            <FaSync className={isRefreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        <button
          onClick={() => {
            setIsModalOpen(true);
            setSelectedECard(null);
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          {t('+ Create New')}
        </button>
      </div>

      {/* Table */}
      <div
        className={`${filteredECards.length > 0 ? 'overflow-x-auto' : 'overflow-hidden'} h-table-height-counter`}
      >
        <table className="w-full">
          <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
            <tr className="border-b border-skin-base/10">
              <th className="sticky left-0 z-10 bg-white bg-skin-primary px-6 py-3 text-center text-sm font-semibold text-skin-base min-w-[50px]">
                {t('No')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">
                {t('Avatar')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[180px]">
                {t('Sip Account')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[120px]">
                {t('Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[120px]">
                {t('URL')}
              </th>
              <th className="sticky right-0 z-10 bg-white px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[100px]">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filteredECards.map((ecard, index) => {
              return (
                <tr key={ecard._id} className="border-t border-skin-base/10">
                  <td className="sticky left-0 z-10 bg-skin-primary px-6 py-4 text-sm text-skin-base text-center">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {ecard.avatar ? (
                        <img
                          src={
                            ecard.avatar.startsWith('http') || ecard.avatar.startsWith('data:')
                              ? ecard.avatar
                              : `${getBaseUrl()}${ecard.avatar}`
                          }
                          alt={ecard.name}
                          className="w-full h-full object-cover"
                          onError={e => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML =
                              '<svg class="text-gray-400" width="14" height="14" fill="currentColor" viewBox="0 0 448 512"><path d="M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z"></path></svg>';
                          }}
                        />
                      ) : (
                        <FaUser className="text-gray-400" size={14} />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    <div className="max-w-[120px] truncate" title={ecard.sipAccount || '--'}>
                      {ecard.sipAccount || '--'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    <div className="max-w-[120px] truncate" title={ecard.name || '--'}>
                      {ecard.name || '--'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    <div
                      className="max-w-[280px] truncate"
                      title={
                        ecard.ecardId ? `${window.location.origin}/ecard?id=${ecard.ecardId}` : '--'
                      }
                    >
                      {ecard.ecardId ? `${window.location.origin}/ecard?id=${ecard.ecardId}` : '--'}
                    </div>
                  </td>
                  <td className="sticky bg-skin-primary right-0 z-10 px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedECard(ecard);
                          setIsModalOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <img src={editIcon} alt="edit" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={e => handleMenuClick(e, ecard)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <FaEllipsisV />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ECardModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedECard(null);
        }}
        onSuccess={() => {
          fetchECards();
        }}
        ecard={selectedECard}
      />

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t('Delete ECard')}
        message={t('Are you sure you want to delete this ECard? This action cannot be undone.')}
        confirmText={t('Delete')}
        cancelText={t('Cancel')}
        type="danger"
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className="mt-2"
        PaperProps={{
          className: 'bg-[#1c1f3b] border-2 border-[#393e591f] rounded-lg shadow-xl app-text',
          elevation: 0,
        }}
      >
        <MenuItem
          onClick={() => {
            window.open(`${window.location.origin}/ecard?id=${selectedECard?.ecardId}`, '_blank');
            handleClose();
          }}
          className="text-sm px-4 py-2 hover:bg-blue-50"
        >
          {t('Preview')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            setIsQRCodeModalOpen(true);
            handleClose();
          }}
          className="text-sm px-4 py-2 hover:bg-blue-50"
        >
          {t('QRCode')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            setIsConfirmOpen(true);
            handleClose();
          }}
          className="text-white hover:bg-[#292f4c]"
        >
          {t('Delete')}
        </MenuItem>
      </Menu>

      <QRCodeModal
        isOpen={isQRCodeModalOpen}
        onClose={() => setIsQRCodeModalOpen(false)}
        qrValue={`${window.location.origin}/ecard?id=${selectedECard?.ecardId}`}
        title={t('ECard QR Code')}
        name={selectedECard?.name}
      />
    </div>
  );
}
